import { generateLevelPairs, mulberry32, seedFromString, type Level } from '@color-dots/game';

const DAILY_KEY = 'color-dots-daily:v1';
const DAILY_SIZE = 6;
const DAILY_PAIRS = 6;

export interface DailyProgress {
  lastCompletedDate: string | null;
  streak: number;
  bestStreak: number;
}

const EMPTY_PROGRESS: DailyProgress = { lastCompletedDate: null, streak: 0, bestStreak: 0 };

/** Player's local calendar date, not UTC — the daily puzzle changes at their own
 *  midnight, not some server's, matching the "no pressure" design principle. */
export function todayKey(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function yesterdayKey(now = new Date()): string {
  const prev = new Date(now);
  prev.setDate(prev.getDate() - 1);
  return todayKey(prev);
}

export function loadDailyProgress(): DailyProgress {
  try {
    const raw = localStorage.getItem(DAILY_KEY);
    if (!raw) return { ...EMPTY_PROGRESS };
    const parsed = JSON.parse(raw) as Partial<DailyProgress>;
    return {
      lastCompletedDate: typeof parsed.lastCompletedDate === 'string' ? parsed.lastCompletedDate : null,
      streak: typeof parsed.streak === 'number' ? parsed.streak : 0,
      bestStreak: typeof parsed.bestStreak === 'number' ? parsed.bestStreak : 0,
    };
  } catch {
    return { ...EMPTY_PROGRESS };
  }
}

function saveDailyProgress(progress: DailyProgress): void {
  localStorage.setItem(DAILY_KEY, JSON.stringify(progress));
}

export function isDailyCompletedToday(): boolean {
  return loadDailyProgress().lastCompletedDate === todayKey();
}

/**
 * Records today's completion. A missed day quietly resets the streak to 1 on
 * the next completion — no "you lost your streak" messaging, no penalty beyond
 * that, consistent with the project's no-pressure principle (doc/01-overview.md).
 * Completing twice in the same day is a no-op (returns the unchanged progress).
 */
export function markDailyCompleted(now = new Date()): DailyProgress {
  const progress = loadDailyProgress();
  const today = todayKey(now);
  if (progress.lastCompletedDate === today) return progress;

  const streak = progress.lastCompletedDate === yesterdayKey(now) ? progress.streak + 1 : 1;
  const next: DailyProgress = {
    lastCompletedDate: today,
    streak,
    bestStreak: Math.max(progress.bestStreak, streak),
  };
  saveDailyProgress(next);
  return next;
}

/** Same puzzle for every player on a given date — seeded from the calendar date
 *  itself, so it's reproducible across devices/sessions without a backend. */
export function generateDailyLevel(now = new Date()): Level {
  const key = todayKey(now);
  const generated =
    generateLevelPairs(DAILY_SIZE, DAILY_PAIRS, 0, mulberry32(seedFromString(key))) ??
    generateLevelPairs(DAILY_SIZE, DAILY_PAIRS, 0, mulberry32(seedFromString(`${key}-retry`)));

  if (!generated) {
    throw new Error(`Failed to generate daily level for ${key}`);
  }

  return {
    id: `daily-${key}`,
    size: DAILY_SIZE,
    difficulty: 'medium',
    pairs: generated.pairs,
  };
}
